import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * The WHITE wordmark, used directly rather than through a theme-aware logo.
 *
 * This panel is dark in BOTH themes, so a theme-derived variant would hand back
 * the ink wordmark on a light-themed page — invisible on ink. Filtering it
 * (brightness-0 invert) makes it visible and destroys it: the mark flattens
 * into a white blob, because a filter cannot know which parts of a logo are the
 * shape and which are the ground. The white asset exists for exactly this.
 */
const BRAND_WORDMARK_WHITE = "/brand/fiscalo-logo-white.svg";

export interface CheckoutSummaryPanelProps {
  amountMinor: number | null;
  currency: string;
  /** The charge itemised, as the SERVER built it. Empty renders the total alone. */
  items: Array<{ title: string; amountMinor: number }>;
  /** Where "volver" goes — the surface the checkout was started from. */
  backHref: string;
}

function formatMinor(amountMinor: number, currency: string): string {
  return `${(amountMinor / 100).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

/**
 * The brand half of a checkout: what is being charged, and the way back.
 *
 * PORTED FROM GESTIONALO's checkout-summary-panel, structure intact (user,
 * 2026-10-28: "copy pixel by pixel the gestionalo one"). Modelled on Stripe's
 * hosted checkout — a full-bleed dark half with the amount as the page's hero,
 * the form on plain ground opposite.
 *
 * THE LAYOUT IS `flex flex-col gap-8`, NOT `justify-between`. Spreading these
 * three blocks across a full-height column is what made the first attempt read
 * as broken: the wordmark pinned to the ceiling, the amount marooned in the
 * middle, the note on the floor, with voids between them. They stack from the
 * top with one gap, and `lg:items-end` pulls the column against the divide so
 * the amount sits beside the form rather than drifting off to the left.
 *
 * Painted with --fx-ink-900 / --fx-paper, the raw brand tokens, NOT
 * --color-primary. The latter flips in dark mode, which would repaint this
 * always-dark panel with the light-theme ink colour.
 */
export function CheckoutSummaryPanel({
  amountMinor,
  currency,
  items,
  backHref,
}: CheckoutSummaryPanelProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-8 bg-[var(--fx-ink-900)] px-6 py-8 text-[var(--fx-paper)] lg:min-h-svh lg:items-end lg:px-10 lg:py-14">
      <div className="w-full lg:max-w-sm">
        <a
          href={backHref}
          className="inline-flex items-center gap-2 rounded-md text-sm text-white/70 underline-offset-4 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <ArrowLeft className="size-4" />
          <img
            src={BRAND_WORDMARK_WHITE}
            alt="Fiscalo"
            className="h-6 w-auto"
          />
          <span className="sr-only">{t("pay.mercadoPagoBack")}</span>
        </a>
      </div>

      {amountMinor == null ? (
        <div className="w-full lg:max-w-sm">
          {/* A placeholder bar rather than a zero: "0,00" while the real
              amount loads is the one wrong number a payment page must not
              show. */}
          <div className="h-10 w-40 animate-pulse rounded bg-white/10" />
        </div>
      ) : (
        <div className="w-full lg:max-w-sm">
          <p className="text-sm text-white/60">{t("pay.mercadoPagoPayFor")}</p>
          <p className="mt-1 text-4xl font-semibold tracking-tight tabular-nums">
            {formatMinor(amountMinor, currency)}
          </p>

          {items.length > 0 ? (
            <dl className="mt-8 space-y-3 border-t border-white/15 pt-6">
              {items.map((item, index) => (
                <div
                  // Derived per request from the checkout row, in a fixed
                  // order, with no ids of their own.
                  key={`${index}-${item.title}`}
                  className="flex items-baseline justify-between gap-6 text-sm"
                >
                  <dt className="min-w-0 text-white/80">
                    <span className="block truncate">{item.title}</span>
                  </dt>
                  <dd className="shrink-0 tabular-nums text-white">
                    {formatMinor(item.amountMinor, currency)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}

          <div className="mt-6 flex items-baseline justify-between gap-6 border-t border-white/15 pt-6">
            <span className="whitespace-nowrap text-sm font-medium text-white">
              {t("pay.mercadoPagoTotalLabel")}
            </span>
            <span className="text-lg font-semibold tabular-nums text-white">
              {formatMinor(amountMinor, currency)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
